import { Numbers, NullChecker } from '@pacem/pacem-foundation';
//namespace Pacem.Mathematics.NumberTheory {
const ALPHABET = '0123456789abcdefghijklmnopqrstuvwxyz';
const OUT_OF_RANGE = `Radix out of range: possible values go between positive 1 exclusive and ${ALPHABET.length} inclusive`;
/** Utility tools about number theory, 'diophantine' stuff... */
export class Utils {
    static lcd(...args) {
        if (NullChecker.isNullOrEmpty(args) || args.length <= 1) {
            throw 'Insufficient set of numbers.';
        }
        function ex9(x, y) {
            if (!y)
                return y === 0 ? x : NaN;
            return ex9(y, x % y);
        }
        function ex10(x, y) {
            return x * y / ex9(x, y);
        }
        let result = Math.round(args[0]);
        for (let j = 1; j < args.length; j++) {
            result = ex10(result, Math.round(args[j]));
        }
        return result;
    }
    static gcd(a, b) {
        // force integers
        a = Math.round(a),
            b = Math.round(b);
        if (a === 0) {
            return b;
        }
        return Utils.gcd(b % a, a);
    }
    static rebaseInt(v, from, to) {
        return Numbers.rebase(v, from, to);
    }
    static rebaseFloat(v, fromRadix, toRadix, precision = 12) {
        const from10 = Utils.rebaseFloat10ToN, to10 = Utils.rebaseFloatNTo10;
        if (fromRadix === 10) {
            const vFloat = parseFloat(v.toString());
            if (toRadix % 1 === 0) {
                return Utils.rebaseFloat10ToNIntBase(vFloat, toRadix, precision);
            }
            return from10(vFloat, toRadix, precision);
        }
        if (toRadix === 10) {
            return to10(v.toString(), fromRadix, precision);
        }
        const _10 = to10(v.toString(), fromRadix, precision + 1);
        return from10(_10, toRadix, precision);
    }
    static rebaseFloatNTo10(v, radix, precision = 12) {
        const alphabet = ALPHABET;
        if (radix <= 1 || radix > alphabet.length) {
            throw new Error(OUT_OF_RANGE);
        }
        const vs = (v ?? '0').toString().toLowerCase(), nan = Number.NaN;
        if (vs === '0') {
            return 0;
        }
        const arr = /^([-+]?) *([\da-z]+)?(.[\da-z]+)?$/.exec(vs);
        if (arr.length < 2) {
            return nan;
        }
        const sign = arr[1] === '-' ? -1 : 1, int = arr[2] || '0', frac = arr[3] || '.0';
        let retval = 0, cursor = 0;
        for (let digit of (int + frac.substr(1))) {
            const num = alphabet.indexOf(digit);
            if (num === -1 || num >= radix) {
                return nan;
            }
            retval += num * Math.pow(radix, int.length - cursor - 1);
            cursor++;
        }
        return sign * retval;
    }
    static rebaseFloat10ToNIntBase(v, radix, precision = 12) {
        const alphabet = ALPHABET;
        if (radix <= 1 || radix > alphabet.length) {
            throw new Error(OUT_OF_RANGE);
        }
        const to = radix;
        let value = Math.abs(v ?? 0);
        if (value === 0 || Number.isNaN(value)) {
            return v.toString();
        }
        let decimals = (value % 1).roundoff();
        let output = '';
        // loop
        do {
            // std
            let div = value / to /* do not roundoff: e.g. 35.99999999999986 / 36 rounds off to 1 */;
            let divFloor = Math.floor(div);
            let mod = (value - divFloor * to).roundoff();
            let intPart = Math.floor(mod);
            if (intPart >= alphabet.length) {
                output = `[${intPart}]` + output;
            }
            else {
                output = alphabet[intPart] + output;
            }
            if (divFloor <= 0) {
                break;
            }
            value = divFloor;
        } while (true);
        output = output.replace(/^0+/, '');
        output ||= '0';
        if (decimals === 0) {
            return output;
        }
        let rebasedFrac = '';
        // loop (same)
        // TODO: tidy up code.
        do {
            value = (decimals * to).roundoff();
            const intPart = Math.floor(value), remainder = (value - intPart).roundoff();
            if (intPart >= alphabet.length) {
                rebasedFrac = `[${intPart}]`;
            }
            else {
                rebasedFrac += alphabet[intPart];
            }
            decimals = remainder;
        } while (!decimals.isCloseTo(0) && rebasedFrac.length < precision);
        if (/^0*$/.test(rebasedFrac)) {
            return output;
        }
        return (Math.sign(v) < 0 ? '-' : '') + output + '.' + rebasedFrac.replace(/0+$/, '');
    }
    static rebaseFloat10ToN(v, radix, precision = 12) {
        const alphabet = ALPHABET;
        if (radix <= 1 || radix > alphabet.length) {
            throw new Error(OUT_OF_RANGE);
        }
        const to = radix;
        let value = Math.abs(v ?? 0);
        if (value === 0 || Number.isNaN(value)) {
            return v.toString();
        }
        let sign = Math.sign(v) < 0 ? '-' : '';
        let int = [];
        let dec = [];
        for (let j = 0; j < 20; j++) {
            int.push('0');
            dec.push('0');
        }
        const maxIndex = to % 1 === 0 ? to : Math.ceil(to);
        function add(integers, j /* cellIndex */, digitIndex) {
            const output = integers ? int : dec;
            const current = output[j] = output[j] || '0';
            const index = alphabet.indexOf(current) + digitIndex, tgetIndex = index % maxIndex, carryIndex = Math.floor(index / maxIndex);
            output[j] = alphabet[tgetIndex];
            if (carryIndex > 0) {
                if (integers) {
                    // integers
                    add(integers, j + 1, carryIndex);
                }
                else {
                    // fractionals
                    if (j === 0) {
                        // carry on integers
                        add(true, 0, carryIndex);
                    }
                    else {
                        add(false, j - 1, carryIndex);
                    }
                }
            }
            return carryIndex;
        }
        let step = 0;
        do {
            const magnitude = Math.floor(Numbers.log(value, to));
            const isInteger = magnitude >= 0;
            const absMagnitude = isInteger ? magnitude : Math.abs(1 + magnitude);
            add(isInteger, absMagnitude, 1);
            value = (value - Math.pow(to, magnitude).roundoff()).roundoff();
            if (!isInteger) {
                step++;
            }
            if (value === 0 || step >= 100) {
                const integer = sign + (int.reverse().map(i => i || '0').join('').replace(/^0+/, '') || '0'), fractional = dec.map(i => i || '0').join('').substr(0, precision).replace(/0+$/, '');
                if (/^0*$/.test(fractional)) {
                    return integer;
                }
                return integer + '.' + fractional;
            }
        } while (true);
    }
}
//}
